import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Flame, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [campuses, setCampuses] = useState([]);
  const [manager, setManager] = useState(null);
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c] = await Promise.all([api.get("/campuses")]);
        setCampuses(c.data);
      } catch {
        /* ignore */
      }
      if (user?.manager_id) {
        try {
          const { data } = await api.get(`/users/${user.manager_id}`);
          setManager(data);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [user]);

  useEffect(() => setPhone(user?.phone || ""), [user]);

  const campusName = campuses.find((c) => c.id === user?.campus_id)?.name || "—";

  const savePhone = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", { phone });
      setUser(data);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser({ ...user, avatar_path: data.avatar_path });
      toast.success("Avatar updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
    e.target.value = "";
  };

  const initials = (user?.name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">Profile</p>
        <h1 className="mt-1 text-4xl font-serif text-forest-900">My Profile</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Avatar */}
        <div className="patria-card p-6 flex flex-col items-center gap-4" data-testid="avatar-card">
          <div className="relative">
            {user?.avatar_path ? (
              <img
                src={fileUrl(user.avatar_path)}
                alt={user.name}
                className="h-32 w-32 rounded-full object-cover"
              />
            ) : (
              <div className="h-32 w-32 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center text-3xl font-serif">
                {initials || "P"}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-1 right-1 h-9 w-9 rounded-full bg-terracotta-500 text-cream-100 flex items-center justify-center hover:bg-terracotta-600 shadow-card"
              aria-label="Upload avatar"
              data-testid="upload-avatar-btn"
            >
              <Camera className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={uploadAvatar}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-forest-900">{user?.name}</p>
            <p className="text-sm text-charcoal-muted">{user?.job_title}</p>
            <p className="mt-2 inline-block patria-chip-terra uppercase tracking-widest">
              {user?.role?.replace("_", " ")}
            </p>
          </div>
        </div>

        {/* Details (read-only for employees; phone editable) */}
        <div className="patria-card p-6 lg:col-span-2 space-y-6" data-testid="profile-details-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <ReadOnlyField label="Full name" value={user?.name} testId="pf-name" />
            <ReadOnlyField label="Work email" value={user?.email} testId="pf-email" />
            <ReadOnlyField label="Campus" value={campusName} testId="pf-campus" />
            <ReadOnlyField label="Department" value={user?.department || "—"} testId="pf-dept" />
            <ReadOnlyField label="Job title" value={user?.job_title || "—"} testId="pf-title" />
            <ReadOnlyField label="Manager" value={manager?.name || "—"} testId="pf-manager" />
            <ReadOnlyField label="Hire date" value={user?.hire_date || "—"} testId="pf-hire" />
            <ReadOnlyField
              label="Employment status"
              value={(user?.employment_status || "").replace("_", " ")}
              testId="pf-status"
            />
          </div>

          <form onSubmit={savePhone} className="border-t border-sage-300/60 pt-6 space-y-3">
            <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft">
              Phone number
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-0100"
                className="patria-input w-full sm:max-w-sm"
                data-testid="phone-input"
              />
              <button
                type="submit"
                disabled={saving}
                className="patria-btn-primary"
                data-testid="save-phone-btn"
              >
                <Save className="h-4 w-4" strokeWidth={1.5} />
                {saving ? "Saving..." : "Save phone"}
              </button>
            </div>
            <p className="text-xs text-charcoal-muted">
              Everything else on your profile is maintained by People Operations. Please
              reach out to a People Operations Admin to update other details.
            </p>
          </form>

          {/* Self-service password change */}
          <ChangePasswordCard />
        </div>
      </div>

      {/* My LIFT History — Section A2: nav pruning surfaces past LIFT here. */}
      <MyLiftHistory />
    </div>
  );
}

function MyLiftHistory() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/lift/my-participations")
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;
  return (
    <section className="patria-card p-6" data-testid="my-lift-history">
      <div className="flex items-center gap-2">
        <Flame className="h-5 w-5 text-terracotta-500" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-forest-900">My LIFT History</h2>
      </div>
      <p className="mt-1 text-xs text-charcoal-muted">
        Every LIFT cycle you&rsquo;ve participated in. Your synthesis packets stay here
        for you to revisit any time.
      </p>
      {rows.length === 0 && (
        <p className="mt-4 text-sm italic text-charcoal-muted" data-testid="lift-history-empty">
          You haven&rsquo;t been part of a LIFT cycle yet. When People Operations kicks
          one off for your role, it&rsquo;ll appear here.
        </p>
      )}
      {rows.length > 0 && (
        <ul className="mt-4 divide-y divide-sage-300/60">
          {rows.map((p) => (
            <li key={p.participation_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`lift-history-row-${p.participation_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-forest-900">{p.cycle_name}</p>
                <p className="text-xs text-charcoal-muted">
                  Cycle status: <span className="capitalize">{(p.cycle_status || "").replace(/_/g, " ")}</span>
                  {p.deadline && ` · deadline ${p.deadline}`}
                </p>
              </div>
              {p.packet_released_at ? (
                <Link
                  to={`/lift/packet/${p.cycle_id}/${user?.id}`}
                  className="patria-btn-secondary text-sm min-h-[44px]"
                  data-testid={`lift-history-packet-${p.participation_id}`}
                >
                  View Packet
                </Link>
              ) : (
                <span className="text-xs italic text-charcoal-muted">
                  Packet not yet released
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New password + confirmation don't match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated. Please use your new password next time you sign in.");
      setOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 border-t border-sage-300/40 pt-6" data-testid="change-password-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-forest-900">Change Password</h3>
          <p className="text-xs text-charcoal-muted">
            Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 number.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="patria-btn-secondary text-sm"
          data-testid="btn-toggle-password-change"
        >
          {open ? "Cancel" : "Change my password"}
        </button>
      </div>
      {open && (
        <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="block text-xs text-charcoal-muted">Current password</span>
            <input
              type="password"
              className="patria-input mt-1"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="pw-current"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-charcoal-muted">New password</span>
            <input
              type="password"
              className="patria-input mt-1"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
              data-testid="pw-new"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-charcoal-muted">Confirm new password</span>
            <input
              type="password"
              className="patria-input mt-1"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              required
              data-testid="pw-confirm"
            />
          </label>
          <div className="sm:col-span-3 flex justify-end">
            <button type="submit" disabled={saving} className="patria-btn-primary" data-testid="btn-submit-password">
              <Save className="h-4 w-4" strokeWidth={1.5} />
              {saving ? "Saving…" : "Save new password"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className="mt-1 text-charcoal font-medium capitalize-first" data-testid={testId}>
        {value || "—"}
      </p>
    </div>
  );
}
